import {useCallback, useEffect, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {isAxiosError} from 'axios'
import {Button} from '@/components/ui/button'
import {Skeleton} from '@/components/ui/skeleton'
import {api} from '@/lib/api'
import type {components} from '@/lib/api-schema'

type Estado = 'cargando' | 'sinPermiso' | 'noEncontrado' | 'borrado' | 'error'

/** Decisión del founder: sin visor en Kairos — se pide el enlace firmado y el navegador abre o descarga. */
export function FeedbackArchivoPage() {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const {reporteId, archivoId} = useParams()
  const [estado, setEstado] = useState<Estado>('cargando')

  const cargar = useCallback(() => {
    let cancelado = false
    setEstado('cargando')
    api
      .get<components['schemas']['FeedbackArchivoEnlace']>(`/admin/feedback/${reporteId}/archivos/${archivoId}`)
      .then(({data}) => {
        if (!cancelado) window.location.replace(data.url)
      })
      .catch((err) => {
        if (cancelado) return
        const status = isAxiosError(err) ? err.response?.status : undefined
        setEstado(
          status === 403 ? 'sinPermiso' : status === 404 ? 'noEncontrado' : status === 410 ? 'borrado' : 'error'
        )
      })
    return () => {
      cancelado = true
    }
  }, [reporteId, archivoId])

  useEffect(() => cargar(), [cargar])

  if (estado === 'cargando') {
    return <Skeleton className="h-24 w-full max-w-md" data-testid="feedback-archivo-cargando" />
  }

  // `errors.objetoAjeno`/`errors.archivoBorrado` ya dicen el 404 y el 410 en el diálogo de feedback;
  // esta pantalla reusa ese mismo cuerpo en vez de duplicarlo.
  const textos: Record<Exclude<Estado, 'cargando'>, {titulo: string; cuerpo: string}> = {
    sinPermiso: {titulo: t('feedback.archivo.sinPermisoTitulo'), cuerpo: t('feedback.archivo.sinPermisoCuerpo')},
    noEncontrado: {titulo: t('feedback.archivo.noEncontradoTitulo'), cuerpo: t('feedback.errors.objetoAjeno')},
    borrado: {titulo: t('feedback.archivo.borradoTitulo'), cuerpo: t('feedback.errors.archivoBorrado')},
    error: {titulo: t('feedback.archivo.errorTitulo'), cuerpo: t('feedback.archivo.errorCuerpo')},
  }
  const {titulo, cuerpo} = textos[estado]

  return (
    <div className="max-w-md space-y-4" data-testid={`feedback-archivo-${estado}`}>
      <h1 className="font-heading text-xl text-foreground">{titulo}</h1>
      <p className="notice-red rounded-lg px-3.5 py-3 text-sm font-medium">{cuerpo}</p>
      {estado === 'error' ? (
        <Button size="sm" variant="outline" onClick={cargar}>
          {t('feedback.archivo.reintentar')}
        </Button>
      ) : (
        <Button size="sm" variant="outline" onClick={() => navigate('/')}>
          {t('feedback.archivo.volver')}
        </Button>
      )}
    </div>
  )
}
